import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Church } from 'lucide-react';
import { useLongPressShare } from '../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

/**
 * Public flyer detail page (route: /flyer/:id).
 * Visitors land here when they tap a shared link from WhatsApp, Facebook, etc.
 * Tap the share button OR long-press the image to re-share.
 */
export default function FlyerDetailPage() {
  const { id } = useParams();
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const longPress = useLongPressShare({
    kind: 'flyer',
    itemId: id,
    title: flyer?.title || 'Vineyard Event',
    description: flyer?.description || '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/flyers/${id}`);
        if (!cancelled) setFlyer(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Flyer not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Build absolute image URL using the current origin so the long-press → "Share image" action
  // hands the user a cccvob.com URL (not a preview/test URL).
  const imageSrc = (() => {
    if (!flyer?.image_url) return '';
    if (flyer.image_url.startsWith('http://') || flyer.image_url.startsWith('https://')) {
      return flyer.image_url;
    }
    if (flyer.image_url.startsWith('/')) {
      return `${window.location.origin}${flyer.image_url}`;
    }
    return `${window.location.origin}/${flyer.image_url}`;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 flex items-center justify-center text-white">
        <div className="animate-pulse">Loading flyer…</div>
      </div>
    );
  }

  if (error || !flyer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 flex flex-col items-center justify-center text-white px-6">
        <Church className="w-16 h-16 text-amber-300 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Flyer not found</h1>
        <p className="text-violet-200 mb-6 text-center">This event link may have expired or been removed.</p>
        <Link to="/" className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors">
          Visit our church online
        </Link>
      </div>
    );
  }

  const eventDate = (() => {
    if (!flyer.event_date) return null;
    try {
      return new Date(flyer.event_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return null; }
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white" data-testid="flyer-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium" data-testid="flyer-detail-back">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-black/30 backdrop-blur border border-white/10"
        >
          {imageSrc && (
            // Long-press the image to open the native share sheet with rich VOB preview card
            <img {...longPress} src={imageSrc} alt={flyer.title} className="w-full max-h-[80vh] object-contain bg-black/40 select-none" draggable />
          )}
          <div className="p-5 sm:p-8">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {eventDate && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-bold">
                  <Calendar className="w-3.5 h-3.5" /> {eventDate}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-violet-100 text-xs font-medium">
                <Church className="w-3.5 h-3.5" /> CCC VOB Parish
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-2">{flyer.title}</h1>
            {flyer.description && (
              <p className="text-violet-100/90 leading-relaxed whitespace-pre-line">{flyer.description}</p>
            )}
            {flyer.link_url && (
              <a
                href={flyer.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 transition-colors font-bold text-sm"
              >
                Learn more →
              </a>
            )}
            <p className="mt-4 text-xs text-violet-200/60 italic">
              Tip: long-press the image above to share.
            </p>
          </div>
        </motion.div>

        <div className="text-center mt-8">
          <Link to="/" className="text-amber-200 hover:text-amber-100 text-sm font-medium">
            ← Back to Celestial Church of Christ — Valley of Berachah
          </Link>
        </div>
      </div>
    </div>
  );
}
